import { catalogSummaryApi } from 'app/src/services/welfareportal'
import { useWebAppContext } from 'app/src/shared/hooks'
import { Button } from 'primereact/button'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { Dialog, type DialogProps } from 'primereact/dialog'
import React, { useState, type LegacyRef, type MutableRefObject } from 'react'

type DialogHeaderProps = {
  title: string
  subTitle?: string
  generalError?: string
  generalErrorRef?: LegacyRef<HTMLDivElement> | undefined
  classNameHeaderContainer?: string
}

type DialogFooterProps = {
  cancelCallback?: () => void
  okCallBack?: () => void
  cancelLabel?: string
  okLabel?: string
  okDisabled?: boolean
}

export function DialogHeader(props: DialogHeaderProps) {
  return (
    <div className={props.classNameHeaderContainer ?? ''}>
      <div className='grid grid-cols-1 pt-2'>
        {props.title && <h2 className='text-2xl font-bold leading-[20px] text-[#323130]'>{props.title}</h2>}
        {props.subTitle && <p className='mt-[20px] text-base font-normal leading-[20px] text-[#323130]'>{props.subTitle}</p>}
      </div>
      <div ref={props.generalErrorRef} role={props.generalError ? 'alert' : ''} className='mb-5 mt-5 text-base text-red-500'>
        {props.generalError && <>{props.generalError}</>}
      </div>
    </div>
  )
}

export function DialogFooter(props: DialogFooterProps) {
  return (
    <div className='flex flex-col flex-wrap content-center items-center pb-2'>
      <div className='flex flex-row flex-wrap content-center items-center gap-2'>
        {props.cancelCallback && (
          <Button
            data-testid='dialog-cancel-btn'
            outlined
            rounded
            label={props.cancelLabel}
            className='min-w-32 text-[14px] font-normal text-[#323130]'
            onClick={props.cancelCallback}
          />
        )}
        {props.okCallBack && (
          <Button
            data-testid='dialog-confirm-btn'
            rounded
            autoFocus
            className='min-w-32 text-[14px] font-normal'
            label={props.okLabel}
            disabled={props.okDisabled}
            onClick={props.okCallBack}
          />
        )}
      </div>
    </div>
  )
}

export function PortalDialog(
  props: React.PropsWithChildren<
    { headerProps?: DialogHeaderProps } & { footerProps?: DialogFooterProps } & {
      firstInputRef?: MutableRefObject<HTMLInputElement | null>
      onHide: () => void
      header?: DialogProps['header']
      ptHeader?: string
      footer?: DialogProps['footer']
      closable?: boolean
    }
  >,
) {
  const [firstDialogMount, setFirstDialogMount] = useState<boolean>(false)

  return (
    <Dialog
      onHide={props.onHide}
      closable={props?.closable === false ? props.closable : true}
      modal
      pt={{
        hooks: {
          useMountEffect: () => {
            if (props.firstInputRef?.current && !firstDialogMount) {
              props.firstInputRef.current.focus()
              setFirstDialogMount(true)
            }
          },
        },
        root: {
          className: 'max-w-[min(857px,85%)] md:min-w-[min(857px,85%)]',
        },
        header: props.ptHeader ? { className: props.ptHeader } : {},
      }}
      header={props.headerProps ? <DialogHeader {...props.headerProps} /> : props.header}
      visible
      footer={props.footerProps ? <DialogFooter {...props.footerProps} /> : props.footer}
      // style={{ maxWidth: 'min(857px,85%)' }}
    >
      {props.children}
    </Dialog>
  )
}

export function PortalConfirmDialog(props: {
  title: string
  body: string | React.ReactNode
  className?: string
  yesText?: string
  yesLoading?: boolean
  cancelText?: string
  reject: () => void
  accept: () => void
}) {
  return (
    <ConfirmDialog
      header={props.title}
      message={props.body}
      onHide={props.reject}
      group='declarative'
      reject={props.reject}
      visible
      accept={props.accept}
      acceptIcon={typeof props.yesLoading !== 'undefined' && props.yesLoading ? 'pi pi-spinner pi-spin' : undefined}
      rejectClassName='min-w-32 p-button-outlined p-button-rounded text-[14px] font-normal text-[#323130]'
      acceptClassName='min-w-32 p-button-rounded text-[14px] font-normal'
      pt={{
        acceptButton: { 'data-testid': 'confirm-dialog-accept-btn' },
        rejectButton: { 'data-testid': 'confirm-dialog-reject-btn' },
        root: { className: props.className ?? 'min-w-[50vw]' },
        footer: {
          className: 'flex content-center justify-center',
        },
      }}
      focusOnShow={true}
      acceptLabel={props.yesText}
      rejectLabel={props.cancelText}
    />
  )
}

export function PortalInformativeDialog(props: { modal: catalogSummaryApi.InformativeBanner['modal']; onHide: () => void }) {
  const { modal, onHide } = props
  return (
    <PortalDialog
      headerProps={{
        title: modal?.title ?? '',
      }}
      onHide={onHide}
      footerProps={{
        okCallBack: onHide,
        okLabel: modal?.buttonLabel,
      }}
    >
      <div
        dangerouslySetInnerHTML={{
          __html: modal?.body ?? '',
        }}
      />
    </PortalDialog>
  )
}

export function CatalogSummaryButtonInformativeDialog(props: {
  showModal: boolean
  setShowModal: (visible: boolean) => void
  pt?: { buttonLabel?: string }
  bannerId: string
}) {
  const { showModal, setShowModal } = props
  const context = useWebAppContext()

  const catalogSummaryDetails = catalogSummaryApi.useCatalogSummaryList({
    pathParams: { templateBasePath: context.userInfo.welfareCampaign.configuration.frontend.templateId },
  })
  return (
    catalogSummaryDetails.data?.informativeBanners?.[props.bannerId] && (
      <>
        <Button
          link
          text
          label={props.pt?.buttonLabel ?? ` ${catalogSummaryDetails.data?.informativeBanners[props.bannerId]?.actionLabel}`}
          pt={{ root: { className: 'underline pb-0 pl-0 items-baseline' } }}
          icon={catalogSummaryDetails.data?.informativeBanners[props.bannerId]?.actionLabelIcon}
          onClick={() => setShowModal(true)}
        />
        {showModal && (
          <PortalInformativeDialog
            modal={catalogSummaryDetails.data?.informativeBanners[props.bannerId]?.modal}
            onHide={() => setShowModal(false)}
          />
        )}
      </>
    )
  )
}
